import PropTypes from "prop-types";
import { Box, Skeleton, Stack, Typography } from "@mui/material";

import { Badge } from "../../../components/Badge";
import { DataTable } from "../../../components/DataTable";
import { UnifiedModal } from "../../../components/UnifiedModal";

const formatDate = (value) => {
  if (!value) return "Sin fecha";
  try {
    return new Date(value).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const formatMonthLabel = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("es-ES", {
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

const formatSubscriptionStatus = (value) =>
  value === "active" ? "Activa" : "Pausada";

const subscriptionStatusColorMap = {
  active: "green",
  paused: "yellow",
};

const formatRecurrencePeriod = (client) => {
  const start = formatMonthLabel(client.recurrence_period_start);
  if (!start) return "Sin generaciones aún";

  if (client.recurrence_period_end_is_current) {
    return `${start} - Actual`;
  }

  const end = formatMonthLabel(client.recurrence_period_end);
  return end ? `${start} - ${end}` : `${start} - Sin fin`;
};

const formatDuration = (months) => {
  if (!months) return "Sin histórico";
  return `${months} ${months === 1 ? "mes" : "meses"}`;
};

const EmptyCardState = ({ text }) => (
  <Box
    sx={{
      borderRadius: "12px",
      border: "1px dashed",
      borderColor: "divider",
      px: 2,
      py: 3,
      textAlign: "center",
      color: "text.secondary",
      fontSize: "0.875rem",
    }}
  >
    {text}
  </Box>
);

EmptyCardState.propTypes = {
  text: PropTypes.string.isRequired,
};

const StatusPill = ({ status }) => (
  <Badge color={subscriptionStatusColorMap[status] || "gray"} size="sm">
    {formatSubscriptionStatus(status)}
  </Badge>
);

StatusPill.propTypes = {
  status: PropTypes.string.isRequired,
};

const SummaryItem = ({ label, value }) => (
  <Box
    sx={{
      borderRadius: "12px",
      border: "1px solid",
      borderColor: "divider",
      px: 2,
      py: 1.5,
      minWidth: 0,
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "1.125rem", fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const recurrentClientShape = PropTypes.shape({
  client_id: PropTypes.number,
  client_name: PropTypes.string,
  agency_id: PropTypes.number,
  agency_name: PropTypes.string,
  subscription_status: PropTypes.string,
  next_run_date: PropTypes.string,
  total_recurrent_projects: PropTypes.number,
  client_since: PropTypes.string,
  first_recurrent_project_date: PropTypes.string,
  last_recurrent_project_date: PropTypes.string,
  recurrence_period_start: PropTypes.string,
  recurrence_period_end: PropTypes.string,
  recurrence_period_end_is_current: PropTypes.bool,
  recurrence_duration_months: PropTypes.number,
});

export function RecurrentProjectsAgencyDetailModal({
  open,
  onClose,
  onExited,
  agency,
  data,
  isLoading,
  error,
}) {
  const clients = Array.isArray(data?.clients) ? data.clients : [];
  const agencyName = data?.agency_name || agency?.agency_name || "Agencia";

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      title={`Detalle de ${agencyName}`}
      maxWidth="lg"
    >
      {isLoading ? (
        <Stack spacing={2}>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr 1fr",
                md: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={`agency-detail-summary-${index}`}
                variant="rounded"
                height={76}
              />
            ))}
          </Box>
          <Skeleton variant="rounded" height={320} />
        </Stack>
      ) : error ? (
        <EmptyCardState text="No pudimos cargar el detalle de esta agencia. Inténtalo de nuevo." />
      ) : !data ? (
        <EmptyCardState text="No hay detalle disponible para esta agencia." />
      ) : (
        <Stack spacing={2}>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr 1fr",
                md: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            <SummaryItem label="Clientes con recurrencia" value={data.total_clients || 0} />
            <SummaryItem label="Clientes activos" value={data.active_clients || 0} />
            <SummaryItem label="Clientes pausados" value={data.paused_clients || 0} />
            <SummaryItem
              label="Proyectos recurrentes"
              value={data.total_recurrent_projects || 0}
            />
          </Box>

          {clients.length === 0 ? (
            <EmptyCardState text="Esta agencia no tiene clientes recurrentes para mostrar." />
          ) : (
            <DataTable
              size="sm"
              tableMinWidth={880}
              rows={clients}
              getRowId={(client) => client.client_id}
              columns={[
                {
                  key: "client",
                  header: "Cliente",
                  render: (client) => (
                    <Stack spacing={0.25}>
                      <Typography sx={{ fontWeight: 600 }}>{client.client_name}</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Cliente desde {formatDate(client.client_since)}
                      </Typography>
                    </Stack>
                  ),
                },
                {
                  key: "status",
                  header: "Estado",
                  render: (client) => <StatusPill status={client.subscription_status} />,
                },
                {
                  key: "period",
                  header: "Periodo",
                  render: (client) => (
                    <>
                      <Typography sx={{ fontWeight: 500 }}>
                        {formatRecurrencePeriod(client)}
                      </Typography>
                      {client.last_recurrent_project_date ? (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Último generado: {formatDate(client.last_recurrent_project_date)}
                        </Typography>
                      ) : null}
                    </>
                  ),
                },
                {
                  key: "months",
                  header: "Meses",
                  cellSx: { fontWeight: 600 },
                  render: (client) => formatDuration(client.recurrence_duration_months),
                },
                {
                  key: "projects",
                  header: "Proyectos",
                  cellSx: { fontWeight: 600 },
                  render: (client) => client.total_recurrent_projects,
                },
                {
                  key: "next_run",
                  header: "Próxima generación",
                  render: (client) =>
                    client.subscription_status === "active"
                      ? formatDate(client.next_run_date)
                      : "En pausa",
                },
              ]}
            />
          )}
        </Stack>
      )}
    </UnifiedModal>
  );
}

RecurrentProjectsAgencyDetailModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onExited: PropTypes.func,
  agency: PropTypes.shape({
    agency_id: PropTypes.number,
    agency_name: PropTypes.string,
  }),
  data: PropTypes.shape({
    agency_id: PropTypes.number,
    agency_name: PropTypes.string,
    total_clients: PropTypes.number,
    active_clients: PropTypes.number,
    paused_clients: PropTypes.number,
    total_recurrent_projects: PropTypes.number,
    clients: PropTypes.arrayOf(recurrentClientShape),
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.any,
};

RecurrentProjectsAgencyDetailModal.defaultProps = {
  open: false,
  onExited: undefined,
  agency: null,
  data: null,
  isLoading: false,
  error: null,
};
